import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@components/ui/Card';
import Badge from '@components/ui/Badge';
import Button from '@components/ui/Button';
import SearchInput from '@components/ui/SearchInput';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import EmptyState from '@components/ui/EmptyState';
import ErrorState from '@components/ui/ErrorState';
import './Projects.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Project {
  id: string;
  project_name: string;
  status: string;
  company_id: string | null;
  company_name: string | null;
  lead_person_id: string | null;
  lead_person_name: string | null;
  estimated_start_date: string | null;
  estimated_close_date: string | null;
  billed_amount: number | null;
  profit: number | null;
  github_repo_status: string;
  created_at: string;
  updated_at: string;
}

interface ProjectsResponse {
  success: boolean;
  data: Project[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

// ─── Status badge variant mapping ────────────────────────────────────────────

const PROJECT_STATUSES = [
  'New',
  'Proposal Accepted',
  'Work Started',
  'Work Finished',
  'Completed',
] as const;

function statusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' {
  switch (status) {
    case 'Completed':
      return 'success';
    case 'Work Started':
    case 'Work Finished':
      return 'warning';
    case 'Proposal Accepted':
      return 'default';
    default:
      return 'default';
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

const Projects: React.FC = () => {
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [companies, setCompanies] = useState<Array<{ id: string; company_name: string }>>([]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch companies for filter dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await fetch('/api/companies?page_size=100');
        if (!res.ok) return;
        const json = await res.json() as { success: boolean; data: Array<{ id: string; company_name: string }> };
        if (json.success) {
          setCompanies(json.data.sort((a, b) => a.company_name.localeCompare(b.company_name)));
        }
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
    };
    fetchCompanies();
  }, []);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (statusFilter) params.set('status', statusFilter);
      if (companyFilter) params.set('company_id', companyFilter);
      params.set('page_size', '50');

      const res = await fetch(`/api/projects?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: ProjectsResponse = await res.json();
      if (!json.success) throw new Error('API returned failure');
      setProjects(json.data);
      setTotal(json.pagination.total);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, companyFilter]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="projects-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Projects</h1>
          {!loading && !error && (
            <span className="page-count">{total} {total === 1 ? 'project' : 'projects'}</span>
          )}
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => navigate('/projects/new')}
        >
          + New project
        </Button>
      </div>

      {/* Filters */}
      <div className="projects-filters">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by project name or company…"
          className="projects-search"
        />
        <select
          className="projects-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {PROJECT_STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          className="projects-status-filter"
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          aria-label="Filter by company"
        >
          <option value="">All companies</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>{c.company_name}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div className="projects-loading">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <Card>
          <ErrorState message={error} onRetry={fetchProjects} />
        </Card>
      ) : projects.length === 0 ? (
        <Card>
          <EmptyState
            title={debouncedSearch || statusFilter || companyFilter ? 'No projects match your filters' : 'No projects yet'}
            message={
              debouncedSearch || statusFilter || companyFilter
                ? 'Try adjusting your search or filter.'
                : 'Add your first project to get started.'
            }
            action={
              !debouncedSearch && !statusFilter && !companyFilter ? (
                <Button variant="primary" size="sm" onClick={() => navigate('/projects/new')}>
                  + New project
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="projects-list">
          {projects.map((project) => (
            <button
              key={project.id}
              className="project-row"
              onClick={() => navigate(`/projects/${project.id}`)}
              type="button"
            >
              <div className="project-row-main">
                <span className="project-name">{project.project_name}</span>
                <Badge variant={statusVariant(project.status)}>{project.status}</Badge>
              </div>
              <div className="project-row-meta">
                {project.company_name && (
                  <span className="project-company">{project.company_name}</span>
                )}
                {project.lead_person_name && (
                  <span className="project-lead">{project.lead_person_name}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;
